/**
 * Supabase Client — Verbinding met de Supabase backend.
 *
 * SETUP:
 * 1. Maak een Supabase project aan op https://supabase.com
 * 2. Vul VITE_SUPABASE_URL en VITE_SUPABASE_PUBLISHABLE_KEY in je .env
 * 3. Voer het SQL schema uit (zie docs/project/SUPABASE_MIGRATION_PLAN.md §2.2)
 * 4. Installeer @supabase/supabase-js (npm install @supabase/supabase-js)
 *
 * DUAL MODE:
 * Dit bestand exporteert de Supabase client EN een `syncMode` config.
 * Wanneer Supabase niet geconfigureerd is of het package niet beschikbaar is,
 * draait alles op localStorage.
 *
 * @see docs/project/SUPABASE_MIGRATION_PLAN.md
 */

import { warn } from '../utils/logger'

// ─── Configuration ────────────────────────────────────────────────────
// Credentials loaded from environment variables (.env file)

const SUPABASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) ||
  ''
const SUPABASE_ANON_KEY =
  (typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY) ||
  ''

// ─── Sync Mode ────────────────────────────────────────────────────────

export type SyncMode = 'local-only' | 'dual' | 'cloud-only'

/**
 * Determines how data is persisted:
 * - 'local-only': Only localStorage (current behavior, default when no Supabase config)
 * - 'dual': localStorage primary + Supabase background sync
 * - 'cloud-only': Supabase primary with localStorage as cache (future)
 */
export const syncMode: SyncMode =
  SUPABASE_URL && SUPABASE_ANON_KEY ? 'dual' : 'local-only'

// ─── Client Creation (lazy, dynamic import) ───────────────────────────
// We use dynamic import so the app doesn't crash if @supabase/supabase-js
// is not installed. The client is initialized lazily on first use.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let supabase: any | null = null
let initAttempted = false
let initPromise: Promise<void> | null = null

async function initSupabase(): Promise<void> {
  if (initAttempted) return
  initAttempted = true

  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return

  try {
    const mod = await import('@supabase/supabase-js')
    supabase = mod.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        storage:
          typeof window !== 'undefined' ? window.localStorage : undefined,
      },
      realtime: {
        params: {
          eventsPerSecond: 2,
        },
      },
    })
  } catch {
    warn(
      'Supabase client not available — @supabase/supabase-js not installed or import failed. Running in local-only mode.',
    )
    supabase = null
  }
}

/**
 * Get the Supabase client instance. Returns null when not configured or package unavailable.
 * Must be awaited — client is initialized lazily via dynamic import.
 */
export async function getSupabaseClient(): Promise<any | null> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null
  if (!initPromise) {
    initPromise = initSupabase()
  }
  await initPromise
  return supabase
}

// ─── Exports ──────────────────────────────────────────────────────────

/**
 * Quick check: is Supabase configured? (Does NOT check if package is available)
 * Use this for fast synchronous checks before attempting async operations.
 */
export function isSupabaseConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY) && syncMode !== 'local-only'
}

/**
 * Quick check: is Supabase configured and available?
 * Synchronous — returns false until the client has been initialized.
 */
export function isSupabaseAvailable(): boolean {
  return supabase !== null && syncMode !== 'local-only'
}

/**
 * Check if we're currently online (basic check).
 * Used by sync engine to decide whether to attempt Supabase calls.
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true
}

// ─── Database Types (generated from schema) ───────────────────────────
// These types match the Supabase tables defined in SUPABASE_MIGRATION_PLAN.md §2.2

export interface DbAccount {
  id: string
  role: 'learner' | 'parent' | 'teacher' | 'admin'
  display_name: string
  username: string | null
  grade: number | null
  birth_year: number | null
  school_name: string | null
  phone_number: string | null
  is_demo: boolean
  is_plus_user: boolean
  link_code: string | null
  has_completed_onboarding: boolean
  onboarding_steps: Record<string, unknown> | null
  created_at: string
  last_active: string
  updated_at: string
}

export interface DbRelationship {
  id: string
  parent_id: string
  child_id: string
  relationship_type: 'parent_child' | 'teacher_student'
  created_at: string
}

export interface DbClassroom {
  id: string
  name: string
  group_level: number
  teacher_id: string
  join_code: string
  created_at: string
  updated_at: string
}

export interface DbClassroomMember {
  classroom_id: string
  learner_id: string
  joined_at: string
}

export interface DbLearnerState {
  learner_id: string
  state: Record<string, unknown>
  version: number
  updated_at: string
}

export interface DbTeacherNote {
  teacher_id: string
  student_id: string
  note: string
  updated_at: string
}

export interface DbLearnerPreferences {
  learner_id: string
  sound: boolean
  reduced_motion: boolean
  daily_goal: number
  weekly_goal: number
  avatar_url: string | null
  djay_variant: string
  updated_at: string
}
